import { toast } from "react-toastify";
import Axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { msalInstance } from "index";
import config from "utils/config";

// Separate axios instance for the myracle-chatbot backend: different service,
// different failure semantics, own 401-retry queue (kept independent from
// services/config.js's serviceInterface on purpose).
//
// The chatbot backend mounts its routes bare (no "/api/v1" prefix) - any
// external path rewriting (e.g. Teams' /chatbot/api/chat) happens upstream
// at the gateway, outside this app. See myracle-chatbot's Readme.md.
const PATH = "/";

export const chatbotApis = {
  CHAT: "chat",
  HISTORY: "history",
  RATING: "rating",
  RESET: "reset",
  RESET_ALL: "reset/all",
};

export const chatbotInterface = Axios.create({
  baseURL: `${document.env.CHATBOT_BASE_URL}${PATH}`,
});

// Root cause (confirmed against @azure/msal-react's own source,
// dist/msal-react.cjs.development.js's useMsalAuthentication): App.js's
// setChatbotAuthToken(result.idToken) call only ever runs when that hook's
// internal login()/ssoSilent() fires, which itself only happens when
// `!isAuthenticated` at the time App first renders. Since App's own body
// (AppRouter, ChatPanel, everything) is wrapped in <AuthenticatedTemplate>
// and only renders once `isAuthenticated` is already true, and any
// returning/already-logged-in session already has a cached MSAL account by
// the time App first renders, `isAuthenticated` is virtually always
// already true there - so that hook's login() never fires, `result` never
// populates, and setChatbotAuthToken is dead code in practice, not a race.
// This affects *every* call through this instance, not just /history - a
// previous, narrower fix that patched getChatHistory alone missed that
// postChatMessage/postRating/postResetSession were equally unauthenticated
// (silently, since those have a Teams-style body-fallback and don't 401).
// Fixed once, centrally: acquire a fresh token directly and attach it here,
// before every single request, the same way services/config.js's
// acquireTokenSilent() already reliably works for the main backend (also
// called directly, never through useMsalAuthentication's result).
// msalInstance caches the token, so this is a cache lookup, not a network
// round trip, once any token for this scope has been acquired at all.
// Dev-only logging helper: never log the whole error.response/error.request
// (both carry the Authorization: Bearer <jwt> header set above), and never
// log anything at all in production builds.
export const logChatbotError = (message, details) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log(message, details);
  }
};

const getChatbotErrorMessage = error => {
  const data = error.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.statusMessage === "string" && data.statusMessage.trim()) {
    return data.statusMessage;
  }
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  return error.response?.statusText || "Chat request failed. Please try again.";
};

chatbotInterface.interceptors.request.use(async requestConfig => {
  const [account] = msalInstance.getAllAccounts();
  if (account) {
    try {
      const { idToken } = await msalInstance.acquireTokenSilent({
        account,
        scopes: config.CHATBOT_SSO_SCOPES,
      });
      requestConfig.headers.Authorization = `Bearer ${idToken}`;
    } catch (e) {
      logChatbotError("Error while acquireTokenSilent (chatbot request)", e);
    }
  }
  return requestConfig;
});

createAuthRefreshInterceptor(chatbotInterface, failedRequest => {
  const account = msalInstance.getAllAccounts();
  return msalInstance
    .acquireTokenSilent({
      account: account[0],
      scopes: config.CHATBOT_SSO_SCOPES,
    })
    .then(response => {
      failedRequest.response.config.headers[
        "authorization"
      ] = `Bearer ${response.idToken}`;
      chatbotInterface.defaults.headers.common[
        "authorization"
      ] = `Bearer ${response.idToken}`;
    })
    .catch(e => {
      logChatbotError("Error while acquireTokenSilent (chatbot)", e);
    })
    .finally(() => Promise.resolve());
});

chatbotInterface.interceptors.response.use(
  response => response,
  error => {
    // Security fix: error.response/error.request both nest the
    // Authorization header attached above - never log them wholesale.
    // Only a safe, non-sensitive subset is logged, and only outside
    // production builds.
    if (error.response) {
      logChatbotError("chatbotInterface.interceptors.response", {
        status: error.response.status,
        url: error.response.config?.url,
        method: error.response.config?.method,
      });
    } else if (error.request) {
      logChatbotError("NO RESPONSE FROM CHATBOT SERVER!", {
        url: error.config?.url,
        method: error.config?.method,
      });
    } else {
      logChatbotError("Generic chatbot error", error.message);
    }
    const requestPath = error.config?.url?.replace(/\/+$/, "").split("/").pop();
    if (!(error.response?.status === 404 && requestPath === chatbotApis.HISTORY)) {
      toast.error(getChatbotErrorMessage(error));
    }
    return Promise.reject(error);
  }
);

export const setChatbotAuthToken = token => {
  chatbotInterface.defaults.headers["Authorization"] = `Bearer ${token}`;
};

// --- Chat -------------------------------------------------------------

export const postChatMessage = ({
  input,
  preFilters,
  documentLimit = 10,
  offset = 0,
  resetHistory,
  sessionId,
}) => {
  return chatbotInterface.post(chatbotApis.CHAT, {
    input,
    question_interface: "WEB_CHAT",
    pre_filters: preFilters,
    document_limit: documentLimit,
    offset,
    reset_history: resetHistory,
    session_id: sessionId,
  });
};

export const getChatHistory = ({ sessionId, limit = 10 }) => {
  return chatbotInterface.get(chatbotApis.HISTORY, {
    params: { session_id: sessionId, limit },
  });
};

export const postRating = ({ entryId, rating, comment }) => {
  return chatbotInterface.post(chatbotApis.RATING, {
    entry_id: entryId,
    rating,
    comment,
  });
};

export const postResetSession = ({ sessionId }) => {
  return chatbotInterface.post(chatbotApis.RESET, { session_id: sessionId });
};

export const postResetAllSessions = () => {
  return chatbotInterface.post(chatbotApis.RESET_ALL);
};

// Bookmarking is NOT owned by the chatbot engine - bookmark logic lives on
// the main backend (services/bookmarks.js, redux/bookmarks.js), same as
// for Offerings/Quals/Business Challenges everywhere else in the app.
// Chatbot documents[].type/.code match that backend's entityType/code
// conventions exactly, so chat results are bookmarked through the same
// existing system instead of a separate chatbot-backend-owned one.
